import { useEffect, useRef, useState } from 'react';

import { useI18n } from '../../../shared/i18n';
import { localePath } from '../../../shared/i18n/routing';
import { cn } from '../../../shared/lib/cn';
import type { PublicAsset } from '../../../shared/lib';
import { AttributionBadge } from '../../projects/components/AttributionBadge';
import type { PublicProject } from '../../projects/publicApi';
import type { PublicService } from '../../services/publicApi';

interface Row {
  key: string;
  kind: 'work' | 'service';
  title: string;
  href: string;
  /** What the preview shows under the title. */
  blurb: string | undefined;
  image: { url: string; alt: string } | null;
  project?: PublicProject;
  year?: number | null;
}

type Tab = 'all' | 'work' | 'service';

export interface WorkIndexProps {
  projects: PublicProject[];
  services: PublicService[];
  /**
   * The label of the work tab. It comes from the settings registry rather than
   * from here, because it is a claim about who built what (LEG-04): the editor
   * words it, and it never says "projects we delivered".
   */
  workLabel: string;
}

/**
 * Interleave so the list reads as one thing rather than two stacked lists: a
 * project, a service, a project, a service. The shorter side runs out first and
 * the rest simply follows.
 */
function interleave<A, B>(first: A[], second: B[]): Array<A | B> {
  const out: Array<A | B> = [];
  const length = Math.max(first.length, second.length);
  for (let i = 0; i < length; i += 1) {
    if (i < first.length) out.push(first[i] as A);
    if (i < second.length) out.push(second[i] as B);
  }
  return out;
}

/**
 * One list for everything the company makes and offers.
 *
 * Every row is a real link, so the list works with no JavaScript and for a
 * keyboard; hovering a row lights it Amber and a small preview card follows the
 * pointer along the list. The preview is a courtesy, not the content: it repeats
 * what the row already says, is hidden from assistive technology, and never
 * appears on a touch screen.
 *
 * A project row always carries its attribution as visible text (LEG-04), so the
 * list never presents prior work as if the company had built it.
 */
export function WorkIndex({ projects, services, workLabel }: WorkIndexProps) {
  const { locale, t, formatNumber } = useI18n();
  const [tab, setTab] = useState<Tab>('all');
  const [active, setActive] = useState<Row | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const frame = useRef(0);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  const workRows: Row[] = projects.map((project) => ({
    key: `p-${project.id}`,
    kind: 'work',
    title: project.title,
    href: localePath(locale, `/projects/${project.slug}`),
    blurb: project.summary,
    image: project.hero_image ? { url: project.hero_image.url, alt: project.hero_image.alt } : null,
    project,
    year: project.year,
  }));
  const serviceRows: Row[] = services.map((service) => ({
    key: `s-${service.id}`,
    kind: 'service',
    title: service.title,
    href: localePath(locale, '/services'),
    blurb: service.short_description,
    image: null,
  }));

  const rows: Row[] =
    tab === 'work' ? workRows : tab === 'service' ? serviceRows : interleave(workRows, serviceRows);

  if (workRows.length === 0 && serviceRows.length === 0) return null;

  const tabs: Array<{ id: Tab; label: string; show: boolean }> = [
    { id: 'all', label: t('common.home.indexAll'), show: true },
    { id: 'work', label: workLabel, show: workRows.length > 0 },
    { id: 'service', label: t('nav.primary.services'), show: serviceRows.length > 0 },
  ];

  const follow = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    const box = boxRef.current;
    const chip = chipRef.current;
    if (!box || !chip) return;
    const { clientX, clientY } = event;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const rect = box.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      // The chip sits to the side of the pointer, on the start side in either
      // direction, so it never covers the row title being read.
      const dir = getComputedStyle(box).direction === 'rtl' ? -1 : 1;
      chip.style.transform = `translate3d(${x + dir * 36 - (dir === -1 ? 288 : 0)}px, ${y - 100}px, 0) rotate(-2deg)`;
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div
        role="group"
        aria-label={t('common.home.indexFilter')}
        className="flex flex-wrap gap-2.5"
      >
        {tabs
          .filter((entry) => entry.show)
          .map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={tab === entry.id}
              onClick={() => {
                setTab(entry.id);
                setActive(null);
              }}
              className={cn(
                'rounded-full border px-5 py-2.5 text-base font-medium transition-colors duration-[--hs-duration-fast]',
                tab === entry.id
                  ? 'border-accent bg-accent font-semibold text-accent-fg'
                  : 'border-border-strong text-text hover:bg-surface-hover',
              )}
            >
              {entry.label}
            </button>
          ))}
      </div>

      <div
        ref={boxRef}
        className="relative"
        onPointerMove={follow}
        onPointerLeave={() => setActive(null)}
      >
        <ul className="border-b border-border-strong">
          {rows.map((row, index) => (
            <li key={row.key}>
              <a
                href={row.href}
                onPointerEnter={(event) => {
                  if (event.pointerType === 'mouse') setActive(row);
                }}
                className="group grid grid-cols-[2.5rem_1fr] items-center gap-x-3 gap-y-1 border-t border-border-strong py-5 transition-colors duration-[--hs-duration-fast] hover:border-transparent hover:bg-carry hover:text-carry-fg focus-visible:bg-carry focus-visible:text-carry-fg sm:h-24 sm:grid-cols-[4rem_1fr_17rem_5rem] sm:-mx-6 sm:px-6 sm:py-0 sm:hover:rounded-[20px] sm:focus-visible:rounded-[20px]"
              >
                <span
                  className="hs-overline group-hover:text-carry-fg group-focus-visible:text-carry-fg"
                  dir="ltr"
                >
                  {formatNumber(index + 1, { minimumIntegerDigits: 2, useGrouping: false })}
                </span>
                <span className="hs-display text-3xl sm:text-5xl">{row.title}</span>
                <span className="col-start-2 flex flex-wrap items-center gap-2 text-base text-text-muted group-hover:text-carry-fg group-focus-visible:text-carry-fg sm:col-start-auto">
                  {row.project ? (
                    <AttributionBadge
                      type={row.project.attribution.type}
                      label={row.project.attribution.badge_label}
                    />
                  ) : (
                    t('common.home.indexService')
                  )}
                </span>
                <span className="hidden text-end text-base tabular-nums sm:block" dir="ltr">
                  {row.kind === 'work' && row.year != null
                    ? formatNumber(row.year, { useGrouping: false })
                    : '→'}
                </span>
              </a>
            </li>
          ))}
        </ul>

        {/* The preview. Decorative: it repeats what the row already says. */}
        <div
          ref={chipRef}
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute start-0 top-0 z-20 hidden w-72 rounded-[22px] border border-text bg-surface p-3 shadow-sm transition-opacity duration-[--hs-duration-fast] sm:block',
            active ? 'opacity-100' : 'opacity-0',
          )}
        >
          {active ? <Preview row={active} /> : null}
        </div>
      </div>
    </div>
  );
}

function Preview({ row }: { row: Row }) {
  const image: Pick<PublicAsset, 'url'> | null = row.image;
  return (
    <>
      <div className="flex h-40 items-center justify-center overflow-hidden rounded-[14px] bg-surface-sunken">
        {image ? (
          <img src={image.url} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="hs-display text-6xl text-border-strong">
            {[...row.title][0]?.toLocaleUpperCase()}
          </span>
        )}
      </div>
      {row.blurb ? (
        <p className="line-clamp-2 px-1.5 pt-3 pb-1 text-sm text-text-muted">{row.blurb}</p>
      ) : null}
    </>
  );
}
